import { useEffect, useState } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components/index";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import {
  ConnectionType,
  GreaseSeperatorPresent,
  InfiltrationSystemPresent,
  InfiltrationSystemType,
  PipeMaterial,
  PipesInsulatedStatus,
  RainWaterTankPresent,
  SepticTankPresent,
  SewageType,
  TankMaterial,
  WaterBuildingInfoDto,
  WaterPressureIncreaseSystemPresent,
  WaterPressureSufficientStatus,
  WaterSoftenerStatus,
  WaterSoftenerType,
} from "shared/types/water-info";
import { Water } from "assets/icons";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { FileLabel } from "shared/types/file";
// Meters verhuisd naar voorzieningen (assets) — sectie verborgen.
// import WaterMeters from "./waterMeters";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import FileLogTable from "shared/components/uploadFileModal/fileLogTable";
import { Files } from "@phosphor-icons/react";

const WaterBuildingInfoComponent = ({
  waterSystemInfo,
  handleWaterSystemInfoChangeInParent,
  handleSaveInParent,
  handleCloseParent,
  onEditClick,
  parentEditMode = true,
  unit,
  building,
}: any) => {
  const [editMode, setEditMode] = useState<boolean>(parentEditMode || true);
  const [editedWaterSystemInfo, setEditedWaterSystemInfo] =
    useState<WaterBuildingInfoDto | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
  const [selectedFileLabel, setSelectedFileLabel] = useState<FileLabel | null>(
    null,
  );
  const [
    droppedSewageInspectionCertificateFile,
    setDroppedSewageInspectionCertificateFile,
  ] = useState(null);
  const [droppedSewagePlan, setDroppedSewagePlan] = useState(null);
  const [
    droppedWaterInstallationCertificate,
    setDroppedWaterInstallationCertificate,
  ] = useState(null);
  const { session } = useSession();

  useEffect(() => {
    console.log(waterSystemInfo);
    setEditedWaterSystemInfo(waterSystemInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  useEffect(() => {
    setEditMode(parentEditMode);
  }, [parentEditMode]);

  const updateWaterSystemInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedWaterSystemInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleWaterSystemInfoChangeInParent) {
      handleWaterSystemInfoChangeInParent({
        ...waterSystemInfo,
        ...updatedFields,
      });
    }
  };

  const handleFileModalClose = async () => {
    setSelectedFileLabel(null);
    setDroppedSewageInspectionCertificateFile(null);
    setDroppedWaterInstallationCertificate(null);
    setDroppedSewagePlan(null);
    setFileModalOpen(false);
  };

  const handleSave = async () => {
    setHasUnsavedChanges(false);

    if (handleSaveInParent) {
      handleSaveInParent();
    } else {
      try {
        await fetchWithAuth(
          `${API_URL}/water-info/building`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedWaterSystemInfo),
          },
          session,
        );
        toast.success("Water systeem informatie succesvol bijgewerkt!");
      } catch (e) {
        console.error(e);
        toast.error(
          "Er is een fout opgetreden bij het bijwerken van de water systeem informatie.",
        );
      }
    }
  };

  const localOnEditClick = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedWaterSystemInfo(waterSystemInfo);
    }
  };

  // const handleUploadForWaterInstallationCertificate = () => {
  //   setSelectedFileLabel(FileLabel.WaterKeuringBinneninstallatie);
  //   setFileModalOpen(true);
  // };

  // const handleDropForSewagePlan = (e: any) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   if (e.dataTransfer.files && e.dataTransfer.files[0]) {
  //     setDroppedSewagePlan(e.dataTransfer.files[0]);
  //     handleUploadSewagePlan();
  //   }
  // };

  // const handleUploadSewagePlan = () => {
  //   setSelectedFileLabel(FileLabel.Rioleringsplan);
  //   setFileModalOpen(true);
  // };

  const getStatusColor = (status: any) => {
    switch (status) {
      case WaterSoftenerStatus.OK:
      case WaterSoftenerStatus.NOT_REQUIRED:
      case InfiltrationSystemPresent.OK:
      case InfiltrationSystemPresent.NOT_REQUIRED:
      case SepticTankPresent.OK:
      case SepticTankPresent.NOT_REQUIRED:
      case RainWaterTankPresent.OK:
      case RainWaterTankPresent.NOT_REQUIRED:
      case GreaseSeperatorPresent.OK:
      case GreaseSeperatorPresent.NOT_REQUIRED:
      case WaterPressureSufficientStatus.OK:
      case PipesInsulatedStatus.OK:
      case WaterSoftenerStatus.OK:
      case SewageType.SEPARATED:
      case ConnectionType.OWN:
      case InfiltrationSystemType.INFILTRATION_WELL:
      case InfiltrationSystemType.INFILTRATION_CRATES:
      case PipeMaterial.PP:
      case PipeMaterial.PVC:
      case PipeMaterial.PE:
      case WaterPressureIncreaseSystemPresent.OK:
      case WaterPressureIncreaseSystemPresent.NOT_REQUIRED:
        return "bg-green-500";
      case PipesInsulatedStatus.NOT_OK:
      case ConnectionType.NEIGHBOR:
      case SewageType.COMBINED:
        return "bg-orange-500";
      case WaterPressureSufficientStatus.NOT_OK:
      case WaterSoftenerStatus.NOT_OK:
      case PipeMaterial.CONCRETE:
      case PipeMaterial.ETERNIT:
      case WaterSoftenerStatus.NOT_OK:
      case InfiltrationSystemPresent.NOT_OK:
      case SepticTankPresent.NOT_OK:
      case RainWaterTankPresent.NOT_OK:
      case GreaseSeperatorPresent.NOT_OK:
      case WaterPressureIncreaseSystemPresent.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const connectFileLogToWaterBuildingInfo = (fileLogData: any) => {
    const { fileLog } = fileLogData;

    if (selectedFileLabel === FileLabel.RioleringKeuringsattest) {
      updateWaterSystemInfo({
        sewageInspectionCertificateFileLogId: fileLog.id,
      });
      updateWaterSystemInfo({ sewageInspectionCertificateFileLog: fileLog });
    }

    if (selectedFileLabel === FileLabel.Rioleringsplan) {
      updateWaterSystemInfo({ sewagePlanFileLogId: fileLog.id });
      updateWaterSystemInfo({ sewagePlanFileLog: fileLog });
    }

    if (selectedFileLabel === FileLabel.WaterKeuringBinneninstallatie) {
      updateWaterSystemInfo({
        waterInstallationCertificateFileLogId: fileLog.id,
      });
      updateWaterSystemInfo({ waterInstallationCertificateFileLog: fileLog });
    }

    setSelectedFileLabel(null); // finally reset the selected file label
  };

  const handleFileUploadSuccess = async (fileLogData: any) => {
    const { fileLog } = fileLogData;
    let updatedField = {};

    console.log(fileLog);

    switch (selectedFileLabel) {
      case FileLabel.RioleringKeuringsattest:
        updatedField = {
          sewageInspectionCertificateFileLogId: fileLog.id,
          sewageInspectionCertificateFileLog: fileLog,
        };
        break;
      case FileLabel.Rioleringsplan:
        updatedField = {
          sewagePlanFileLogId: fileLog.id,
          sewagePlanFileLog: fileLog,
        };
        break;
      // Add more cases as needed
      default:
        return;
    }

    const updatedWaterSystemInfo = { ...waterSystemInfo, ...updatedField };

    // Save the updated information to the backend
    try {
      const response = await fetchWithAuth(
        `${API_URL}/water-info/building`,
        {
          method: "POST", // or POST, depending on your API
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedWaterSystemInfo),
        },
        session,
      );

      if (response.ok) {
        toast.success("Water information updated successfully");
        setHasUnsavedChanges(false); // Reset unsaved changes state
        setEditedWaterSystemInfo(updatedWaterSystemInfo); // Update local state
      } else {
        throw new Error("Failed to update water information");
      }
    } catch (error) {
      console.error("Error updating water information:", error);
      toast.error("Failed to update water information");
    }
  };

  const onUpdateWaterSystemInfoEasements = (easementsInfo: string) => {
    updateWaterSystemInfo({ easements: easementsInfo });
  };

  const onUpdateWaterSystemInfoSewageRemarks = (remarks: string) => {
    updateWaterSystemInfo({ sewageRemarks: remarks });
  };

  const onUpdateWaterSystemInfoSupplyRemarks = (remarks: string) => {
    updateWaterSystemInfo({ waterSupplyRemarks: remarks });
  };

  return (
    <section
      key="waterSystem"
      className="flex flex-col gap-0 rounded-md bg-white"
    >
      <SectionHeader
        title="Waterhuishouding"
        icon={<Water />} // Ensure this is the correct icon component
        editMode={editMode}
        onEditClick={onEditClick || localOnEditClick}
        onSaveClick={() => handleSave()}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        showEditToggle={true}
        sticky={false}
        hasUnsavedChanges={hasUnsavedChanges}
        hideCloseAction={true}
      />

      {/* Afvoer Section */}
      <div className="p-3">
        <h2 className="font-bold text-lg">Afvoer</h2>
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* Sewage Type */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.sewageType,
                )}`}
              />
              <p>Riolering gescheiden?</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.sewageType || ""}
              options={Object.values(SewageType).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(sewageType) =>
                updateWaterSystemInfo({ sewageType: sewageType })
              }
            />
          </div>

          {/* Connection Type */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.connectionType,
                )}`}
              />
              <p>Eigen aansluiting of via buren?</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.connectionType || ""}
              options={Object.values(ConnectionType).map((value) => ({
                label: value,
                value,
              }))}
              editMode={editMode}
              onChange={(connectionType) =>
                updateWaterSystemInfo({ connectionType: connectionType })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.easements,
                )}`}
              />
              <p>
                Erfdienstbaarheden op riolering (als heersend of lijdend erf)
              </p>
            </header>
            <EditableTextareaComponent
              label="Licht toe welke erfdienstbaarheid werd gevestigd en waar deze staat beschreven"
              value={editedWaterSystemInfo?.easements || ""}
              isLoading={false}
              editMode={editMode}
              onChange={onUpdateWaterSystemInfoEasements}
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.sewageRemarks,
                )}`}
              />
              <p>Algemene opmerkingen riolering</p>
            </header>
            <EditableTextareaComponent
              label="Geef hier algemene nuttige informatie weer"
              value={editedWaterSystemInfo?.sewageRemarks || ""}
              isLoading={false}
              editMode={editMode}
              onChange={onUpdateWaterSystemInfoSewageRemarks}
            />
          </div>

          {/* Pipe Material */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.pipeMaterial,
                )}`}
              />

              <p>Materiaal buizen</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.pipeMaterial || ""}
              options={Object.values(PipeMaterial).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(pipeMaterial) =>
                updateWaterSystemInfo({ pipeMaterial: pipeMaterial })
              }
            />
          </div>

          {/* Main Drain Diameter */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Diameter hoofdafvoer</p>
            </header>
            <EditableInputComponent
              label="Diameter (in mm)"
              value={(editedWaterSystemInfo?.mainDrainDiameter as any) || ""}
              isLoading={false}
              editMode={editMode}
              onChange={(mainDrainDiameter) =>
                updateWaterSystemInfo({ mainDrainDiameter: mainDrainDiameter })
              }
            />
          </div>

          {/* Infiltration System Present */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Infiltratievoorziening aanwezig</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.infiltrationSystemPresent || ""}
              options={Object.values(InfiltrationSystemPresent).map(
                (value) => ({
                  label: value,
                  value: value,
                }),
              )}
              editMode={editMode}
              onChange={(infiltrationSystemPresent) =>
                updateWaterSystemInfo({
                  infiltrationSystemPresent: infiltrationSystemPresent,
                })
              }
            />
          </div>

          {/* Infiltration System Type */}
          {editedWaterSystemInfo?.infiltrationSystemPresent ==
            InfiltrationSystemPresent.OK && (
              <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                  <p>Infiltratievoorziening systeem</p>
                </header>
                <EditableSelectComponent
                  label="Kies een optie"
                  value={editedWaterSystemInfo?.infiltrationSystemType || ""}
                  options={Object.values(InfiltrationSystemType).map((value) => ({
                    label: value,
                    value: value,
                  }))}
                  editMode={editMode}
                  onChange={(infiltrationSystemType) =>
                    updateWaterSystemInfo({
                      infiltrationSystemType: infiltrationSystemType,
                    })
                  }
                />
              </div>
            )}

          {/* Rainwater Tank Present? */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Regenwaterput aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.rainWaterTankPresent || ""}
              options={Object.values(RainWaterTankPresent).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(rainWaterTankPresent) =>
                updateWaterSystemInfo({
                  rainWaterTankPresent: rainWaterTankPresent,
                })
              }
            />
          </div>

          {/* Rainwater Tank Volume */}
          {editedWaterSystemInfo?.rainWaterTankPresent ==
            RainWaterTankPresent.OK && (
              <>
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Volume regenwaterput</p>
                  </header>
                  <EditableInputComponent
                    label="Volume (in liters)"
                    value={
                      (editedWaterSystemInfo?.rainwaterTankVolume as any) || ""
                    }
                    isLoading={false}
                    editMode={editMode}
                    onChange={(rainwaterTankVolume) =>
                      updateWaterSystemInfo({
                        rainwaterTankVolume: rainwaterTankVolume,
                      })
                    }
                  />
                </div>

                {/* Rainwater Tank Location */}
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Locatie regenwaterput</p>
                  </header>
                  <EditableInputComponent
                    label="Locatie"
                    value={editedWaterSystemInfo?.rainwaterTankLocation || ""}
                    isLoading={false}
                    editMode={editMode}
                    onChange={(rainwaterTankLocation) =>
                      updateWaterSystemInfo({
                        rainwaterTankLocation: rainwaterTankLocation,
                      })
                    }
                  />
                </div>

                {/* Rainwater Tank Material */}
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Materiaal regenwaterput</p>
                  </header>
                  <EditableSelectComponent
                    label="Kies een materiaal"
                    value={editedWaterSystemInfo?.rainwaterTankMaterial || ""}
                    options={Object.values(TankMaterial).map((value) => ({
                      label: value,
                      value: value,
                    }))}
                    editMode={editMode}
                    onChange={(rainwaterTankMaterial) =>
                      updateWaterSystemInfo({
                        rainwaterTankMaterial: rainwaterTankMaterial,
                      })
                    }
                  />
                </div>
              </>
            )}

          {/* Septic Tank Present? */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Septische put aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.septicTankPresent || ""}
              options={Object.values(SepticTankPresent).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(septicTankPresent) =>
                updateWaterSystemInfo({ septicTankPresent: septicTankPresent })
              }
            />
          </div>

          {editedWaterSystemInfo?.septicTankPresent == SepticTankPresent.OK && (
            <>
              {/* Septic Tank Volume */}
              <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                  <p>Volume septische put</p>
                </header>
                <EditableInputComponent
                  label="Volume (in liters)"
                  value={(editedWaterSystemInfo?.septicTankVolume as any) || ""}
                  isLoading={false}
                  editMode={editMode}
                  onChange={(septicTankVolume) =>
                    updateWaterSystemInfo({
                      septicTankVolume: septicTankVolume,
                    })
                  }
                />
              </div>

              {/* Septic Tank Location */}
              <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                  <p>Locatie septische put</p>
                </header>
                <EditableInputComponent
                  label="Locatie"
                  value={editedWaterSystemInfo?.septicTankLocation || ""}
                  isLoading={false}
                  editMode={editMode}
                  onChange={(septicTankLocation) =>
                    updateWaterSystemInfo({
                      septicTankLocation: septicTankLocation,
                    })
                  }
                />
              </div>

              {/* Septic Tank Material */}
              <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                  <p>Materiaal septische put</p>
                </header>
                <EditableSelectComponent
                  label="Kies een materiaal"
                  value={editedWaterSystemInfo?.septicTankMaterial || ""}
                  options={Object.values(TankMaterial).map((value) => ({
                    label: value,
                    value: value,
                  }))}
                  editMode={editMode}
                  onChange={(septicTankMaterial) =>
                    updateWaterSystemInfo({
                      septicTankMaterial: septicTankMaterial,
                    })
                  }
                />
              </div>
            </>
          )}

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Vetafscheider aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.greaseSeparatorPresent || ""}
              options={Object.values(GreaseSeperatorPresent).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(greaseSeparatorPresent) =>
                updateWaterSystemInfo({
                  greaseSeparatorPresent: greaseSeparatorPresent,
                })
              }
            />
          </div>

          {/* Grease Separator Location */}
          {editedWaterSystemInfo?.greaseSeparatorPresent ==
            GreaseSeperatorPresent.OK && (
              <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                  <p>Locatie vetafscheider</p>
                </header>
                <EditableInputComponent
                  label="Locatie"
                  value={editedWaterSystemInfo?.greaseSeparatorLocation || ""}
                  isLoading={false}
                  editMode={editMode}
                  onChange={(greaseSeparatorLocation) =>
                    updateWaterSystemInfo({
                      greaseSeparatorLocation: greaseSeparatorLocation,
                    })
                  }
                />
              </div>
            )}
          {/* Additional fields and components */}

          <ImagesContainer
            buildingId={building.id as string}
            fileLabel={FileLabel.SewagePicture}
          />

          {/* Repeat for any additional file upload sections */}
        </div>
      </div>

      {/* Aanvoer Section */}
      <div className="p-3">
        <h2 className="font-bold text-lg">Aanvoer</h2>
        <div className="gap-3 sm:grid sm:grid-cols-2">
          {/* Water Pressure Sufficient Status */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.waterPressureSufficientStatus,
                )}`}
              />

              <p>Is er voldoende druk op het water</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.waterPressureSufficientStatus || ""}
              options={Object.values(WaterPressureSufficientStatus).map(
                (value) => ({
                  label: value,
                  value: value,
                }),
              )}
              editMode={editMode}
              onChange={(waterPressureSufficientStatus) =>
                updateWaterSystemInfo({
                  waterPressureSufficientStatus: waterPressureSufficientStatus,
                })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.waterSupplyRemarks,
                )}`}
              />
              <p>Algemene opmerkingen aanvoer water</p>
            </header>
            <EditableTextareaComponent
              label="Geef hier algemene nuttige informatie weer"
              value={editedWaterSystemInfo?.waterSupplyRemarks || ""}
              isLoading={false}
              editMode={editMode}
              onChange={onUpdateWaterSystemInfoSupplyRemarks}
            />
          </div>

          {/* Main Valve Location */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Locatie hoofdkraan</p>
            </header>
            <EditableInputComponent
              label="Locatie"
              value={editedWaterSystemInfo?.mainValveLocation || ""}
              isLoading={false}
              editMode={editMode}
              onChange={(mainValveLocation) =>
                updateWaterSystemInfo({ mainValveLocation: mainValveLocation })
              }
            />
          </div>

          {/* Pipes Insulated Status */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.pipesInsulatedStatus,
                )}`}
              />

              <p>Isolatie van leidingen</p>
            </header>
            <EditableSelectComponent
              label="Kies een optie"
              value={editedWaterSystemInfo?.pipesInsulatedStatus || ""}
              options={Object.values(PipesInsulatedStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(pipesInsulatedStatus) =>
                updateWaterSystemInfo({ pipesInsulatedStatus })
              }
            />
          </div>

          {/* Water Softener Status */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.waterPressureIncreaseSystemPresent,
                )}`}
              />
              <p>Water drukverhogingssysteem aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Kies een status"
              value={
                editedWaterSystemInfo?.waterPressureIncreaseSystemPresent || ""
              }
              options={Object.values(WaterPressureIncreaseSystemPresent).map(
                (value) => ({
                  label: value,
                  value: value,
                }),
              )}
              editMode={editMode}
              onChange={(waterPressureIncreaseSystemPresent) =>
                updateWaterSystemInfo({ waterPressureIncreaseSystemPresent })
              }
            />
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
              <p>Water pH-waarde</p>
            </header>
            <EditableInputComponent
              label="Vul pH waarde in"
              value={editedWaterSystemInfo?.waterPhValue || ""}
              isLoading={false}
              editMode={editMode}
              onChange={(waterPhValue) =>
                updateWaterSystemInfo({ waterPhValue })
              }
            />
          </div>

          {/* Water Softener Status */}
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedWaterSystemInfo?.waterSoftenerStatus,
                )}`}
              />
              <p>Waterontharder aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Kies een status"
              value={editedWaterSystemInfo?.waterSoftenerStatus || ""}
              options={Object.values(WaterSoftenerStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              editMode={editMode}
              onChange={(waterSoftenerStatus) =>
                updateWaterSystemInfo({ waterSoftenerStatus })
              }
            />
          </div>

          {/* Water Softener Brand */}
          {editedWaterSystemInfo?.waterSoftenerStatus ==
            WaterSoftenerStatus.OK && (
              <>
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Merk waterontharder</p>
                  </header>
                  <EditableInputComponent
                    label="Merk"
                    value={editedWaterSystemInfo?.waterSoftenerBrand || ""}
                    isLoading={false}
                    editMode={editMode}
                    onChange={(waterSoftenerBrand) =>
                      updateWaterSystemInfo({ waterSoftenerBrand })
                    }
                  />
                </div>

                {/* Water Softener Type */}
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Type waterontharder</p>
                  </header>
                  <EditableSelectComponent
                    label="Kies een type"
                    value={editedWaterSystemInfo?.waterSoftenerType || ""}
                    options={Object.values(WaterSoftenerType).map((value) => ({
                      label: value,
                      value: value,
                    }))}
                    editMode={editMode}
                    onChange={(waterSoftenerType) =>
                      updateWaterSystemInfo({ waterSoftenerType })
                    }
                  />
                </div>

                {/* Water Softener Year */}
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Jaar van waterontharder</p>
                  </header>
                  <EditableInputComponent
                    label="Jaar"
                    value={
                      (editedWaterSystemInfo?.waterSoftenerYear as any) || ""
                    }
                    isLoading={false}
                    editMode={editMode}
                    onChange={(waterSoftenerYear) =>
                      updateWaterSystemInfo({ waterSoftenerYear })
                    }
                  />
                </div>

                {/* Water Softener Capacity */}
                <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                  <header className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-block h-5 w-5 rounded-full border border-neutral-primary/30" />
                    <p>Capaciteit waterontharder</p>
                  </header>
                  <EditableInputComponent
                    label="Capaciteit (in liter)"
                    value={
                      (editedWaterSystemInfo?.waterSoftenerCapacity as any) || ""
                    }
                    isLoading={false}
                    editMode={editMode}
                    onChange={(waterSoftenerCapacity) =>
                      updateWaterSystemInfo({ waterSoftenerCapacity })
                    }
                  />
                </div>
              </>
            )}


          {/*
            Meters worden nu beheerd als voorzieningen (assets), niet meer hier.
            Sectie verborgen — zie Instellingen > "Meters in documenten".
            {waterSystemInfo && waterSystemInfo.id && (
              <WaterMeters
                meters={waterSystemInfo.waterMeters}
                incomingWaterBuildingInfoId={waterSystemInfo && waterSystemInfo.id}
              />
            )}
          */}


        </div>
        <div key="water-docs" className="col-span-full mt-8 flex flex-col gap-3">
          <header className="flex justify-between px-2.5">
            <div className="flex items-center gap-2">
              <Files className="h-[18px] w-[18px]" />
              <p className="text-lg">Documenten waterhuishouding</p>
            </div>
          </header>
          <FileLogTable
            building={building as any}
            acceptedLabels={[FileLabel.Rioleringsplan, FileLabel.RioleringKeuringsattest, FileLabel.WaterKeuringBinneninstallatie]}
            onUploadClick={() => setFileModalOpen(true)}
          />
        </div>
      </div>

      <ModalUploadFileLog
        isOpen={fileModalOpen}
        onClose={handleFileModalClose}
        onSave={connectFileLogToWaterBuildingInfo}
        defaultFileLabel={selectedFileLabel as FileLabel}
        customLabels={[selectedFileLabel]}
        unit={unit}
        building={building}
        onFileUploadSuccess={handleFileUploadSuccess}
        droppedFile={
          (selectedFileLabel === FileLabel.RioleringKeuringsattest &&
            droppedSewageInspectionCertificateFile) ||
          (selectedFileLabel === FileLabel.Rioleringsplan &&
            droppedSewagePlan) ||
          (selectedFileLabel === FileLabel.WaterKeuringBinneninstallatie &&
            droppedWaterInstallationCertificate)
        }
      />
      {/* ModalUploadFileLog and any other additional UI elements */}
    </section>
  );
};

export default WaterBuildingInfoComponent;
